import React, { useContext, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { CONTROL_STATUS } from '@trustero/trustero-api-web/lib/model/control_pb'
import { useHover } from '../../Utils/useHover'
import { ProgressContext } from '../../context/ProgressContext'
import { shownToast } from '../../context/Progress/helpers'
import { controlStatusTitle } from '../../lib/common/types'
import { showInfoToast } from '../../Utils/helpers/toast'
import { useControlStats } from '../async/model/control/useControls'
import { Spinner } from '../../Throbber'
import {
  Breakdown,
  Count,
  Left,
  MoreDetails,
  Percentage,
  ProgressBar,
  ProgressDetails,
  ProgressMapCelebrateIcon,
  ProgressMapContainer,
  ProgressMapTitle,
  ProgressPercent,
  Right,
  SpinnerContainer,
  Total,
} from './styles'

const ProgressToast = ({
  percentageCompleted,
}: {
  percentageCompleted: number
}): JSX.Element => (
  <div>
    <ProgressMapCelebrateIcon />
    Hooray! You&apos;ve made
    <ProgressPercent>{percentageCompleted}%</ProgressPercent>
    progress getting your controls ready!
  </div>
)

type Props = {
  attentionNeededCount: number
  auditReadyCount: number
  acceptedCount: number
  readyForReviewCount: number
  failedTest: number
  notApplicableCount: number
}

const ProgressMapWidget = (props: Props): JSX.Element => {
  const { pageContext } = useParams()
  const { progress, setProgress } = useContext(ProgressContext)
  const [isHovered, setIsHovered] = useState(false)
  const handleHover = useHover(isHovered, setIsHovered, 200, 800)

  const counts: number[] = [
    props.attentionNeededCount, // CONTROL_STATUS.NEEDSATTENTION
    props.auditReadyCount, // CONTROL_STATUS.AUDITREADY
    props.acceptedCount, // CONTROL_STATUS.ACCEPTED
    props.readyForReviewCount, // CONTROL_STATUS.READYFORREVIEW
    props.failedTest, // CONTROL_STATUS.FAILEDTEST
    props.notApplicableCount, // CONTROL_STATUS.NOTAPPLICABLE
  ]
  const total = counts.reduce((acc: number, count: number) => {
    acc += count
    return acc
  }, 0)
  const percentageCompleted =
    total > 0
      ? Math.round(((total - props.attentionNeededCount) / total) * 100)
      : -1
  const percentMsg = `${percentageCompleted < 0 ? '...' : percentageCompleted}%`

  useEffect(() => {
    if (progress.showToast) {
      shownToast(progress, setProgress)
      showInfoToast(<ProgressToast percentageCompleted={percentageCompleted} />)
    }
  }, [progress, setProgress, percentageCompleted])

  return (
    <ProgressMapContainer {...handleHover}>
      <ProgressMapTitle />
      <ProgressBar percentage={percentageCompleted} />
      <Percentage>{percentMsg}</Percentage>
      <ProgressDetails percentage={percentageCompleted} isHovered={isHovered}>
        <Breakdown>
          <Total>
            <Left>Total Controls</Left>
            <Right>{total}</Right>
          </Total>
          {counts.map((count: number, idx: number) => {
            const status = idx as CONTROL_STATUS
            return (
              <Count key={`${idx}`} status={status}>
                <Left>{controlStatusTitle[status]}</Left>
                <Right>{count}</Right>
              </Count>
            )
          })}
        </Breakdown>
        <MoreDetails>
          <Link to={`/${pageContext}/controls`}>View Details</Link>
        </MoreDetails>
      </ProgressDetails>
    </ProgressMapContainer>
  )
}

export const ProgressMap = (): JSX.Element => {
  const { data, isLoading } = useControlStats()

  if (isLoading || !data) {
    return (
      <SpinnerContainer>
        <Spinner color="primary" size="s" />
      </SpinnerContainer>
    )
  }

  // Stats list returns an array for all control stats within an audit period. In this case,
  // we are only concerned with the first (and only) control
  const stats = data.getStatsList()[0]

  return (
    <ProgressMapWidget
      failedTest={stats.getNumfailedtest()}
      attentionNeededCount={stats.getNumneedsattention()}
      auditReadyCount={stats.getNumauditready()}
      readyForReviewCount={stats.getNumreadyforreview()}
      acceptedCount={stats.getNumaccepted()}
      notApplicableCount={stats.getNumnotapplicable()}
    />
  )
}
